<template>
  <b-card
    class="no-padding"
    no-body
  >
    <div class="section-block m-2">
      <div class="site-hardware-actions site-hardware-actions_management mb-2">

        <div class="section-block__title mb-0">
          <h2>Site hardware</h2>
        </div>
        <div class="double-button">
          <div
            v-if="(searchFields && searchFields.length > 0) || (orderBy && orderBy.length > 0) || (sortedBy && sortedBy.length > 0)"
            class="d-flex justify-content-end align-items-center"
          >
            <b-button
              variant="primary"
              class="reset-filters-button"
              @click="handleReset"
            >
              <feather-icon
                icon="XIcon"
                size="12"
                stroke="#f0554e"
                class="text-body align-middle mr-25"
              />
              Reset filters
            </b-button>
          </div>
          <b-button
            v-b-tooltip.hover
            variant="link"
            class="pl-1 pr-1"
            title="Refresh hardware information"
            @click="refreshHardwareStatus"
          >
            <feather-icon
              icon="RefreshCcwIcon"
              size="18"
              :class="{ 'spinner-icon': isRefreshing }"
            />
          </b-button>
          <b-button
            v-if="$can('create', 'hardware')"
            v-b-modal.modal-add-hardware
            variant="primary"
          >
            Add hardware to site
          </b-button>
        </div>
      </div>
      <AddHardwareModal
        :added-items="items"
        :update="fetchItems"
      />
      <table-mobile-solo
        :search="searchValue"
        :handle-search="handleMobileSearch"
        :mobile-search-fields="mobileSearchFields"
        :loading="isLoading"
        :items="items"
        :fields="fields"
        :pagination="{
          pagination: pagination,
          perPage: perPage,
          currentPage: page,
          pageSizeOptions: pageSizeOptions,
          enabled: false,
          perPageChanged: perPageChanged,
          changePage: changePage,
        }"
        :rules="{
          model(val) {
            return val.name
          },
          licence(val) {
            return val.name
          },
        }"
        :actions="[{
          label: 'Edit',
          type: 'primary',
          icon: 'Edit2Icon',
          visible: () => this.$can('update', 'hardware'),
          click: (item) => toEdit(item.id),
        }, {
          label: 'Disassociate',
          type: 'danger',
          icon: 'TrashIcon',
          visible: () => true,
          click: (item) => hardwareToDelete = item.id,
        }, {
          label: 'To Port Status',
          type: 'primary',
          icon: 'Link2Icon',
          visible: (item) => item.model.type.id === 'switch' || item.model.type.id === 'controller-standard',
          click: (item) => toSwitchPorts(item.id),
        }, {
          label: 'To CELL Info',
          type: 'primary',
          icon: 'Link2Icon',
          visible: (item) => ['controller-combined', 'odu'].includes(item.model.type.id),
          click: (item) => toLteStats(item.id),
        }, {
          label: 'Download config file',
          type: 'primary',
          icon: 'DownloadIcon',
          visible: (item) => item.model.config_file,
          click: (item) => openNewWindow(item.model.config_file),
        }, {
          label: 'Download build file',
          type: 'primary',
          icon: 'DownloadIcon',
          visible: (item) => item.model.dpk_file,
          click: (item) => openNewWindow(item.model.dpk_file),
        }]"
      />
      <b-card-body>
        <vue-good-table
          ref="VueGoodTable"
          :is-loading="isLoading"
          compact-mode
          :columns="fields"
          :rows="items"
          :total-rows="pagination.total"
          mode="remote"
          :sort-options="{
            enabled: true,
            multipleColumns: true,
            initialSortBy: {field: orderBy, type: sortedBy}
          }"
          :pagination-options="{
            enabled: true,
            perPage: perPage,
            setCurrentPage: page
          }"
          class="hardware-table-status hide-mobile-table"
          @on-sort-change="onSortChange"
          @on-column-filter="columnFilterFn"
        >
          <template slot="loadingContent">
            <b-spinner
              variant="primary"
              label="Spinning"
            />
          </template>
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'action'">
              <span>
                <b-dropdown
                  variant="link"
                  toggle-class="text-decoration-none"
                  no-caret
                >
                  <template v-slot:button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="text-body align-middle mr-25"
                    />
                  </template>
                  <b-dropdown-item
                    v-if="$can('update', 'hardware')"
                    @click.prevent="toEdit(props.row.id)"
                  >
                    <feather-icon
                      icon="Edit2Icon"
                      class="mr-50"
                    />
                    <span>Edit</span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    @click.prevent="hardwareToDelete = props.row.id"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      class="mr-50"
                    />
                    <span>Disassociate</span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="props.row.model.type.id === 'switch' || props.row.model.type.id === 'controller-standard'"
                    @click.prevent="toSwitchPorts(props.row.id)"
                  >
                    <feather-icon
                      icon="Link2Icon"
                      class="mr-50"
                    />
                    <span>Port Status</span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="['controller-combined', 'odu'].includes(props.row.model.type.id)"
                    @click.prevent="toLteStats(props.row.id)"
                  >
                    <feather-icon
                      icon="Link2Icon"
                      class="mr-50"
                    />
                    <span>CELL Info</span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="props.row.model.config_file"
                    :href="props.row.model.config_file"
                    exact-path
                    target="_blank"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      class="mr-50"
                    />
                    <span>Download config file</span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="props.row.model.dpk_file"
                    :href="props.row.model.dpk_file"
                    exact-path
                    target="_blank"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      class="mr-50"
                    />
                    <span>Download build file</span>
                  </b-dropdown-item>
                </b-dropdown>
              </span>
            </span>
            <span v-else-if="props.column.field === 'name'">
              <button
                v-if="props.row.model.manufacturer.name === 'ZyXEL'"
                class="manufacture-disable-stats"
                @click="handleZyXELInfo"
              >
                {{ props.formattedRow[props.column.field] }}
              </button>
              <router-link
                v-else-if="['controller-combined', 'odu'].includes(props.row.model.type.id)"
                :to="`/site/${id}/hardware/lte/${props.row.id}`"
              >
                {{ props.formattedRow[props.column.field] }}
              </router-link>
              <router-link
                v-else-if="props.row.model.type.id === 'switch'"
                :to="`/site/hardware/network/manage?site=${id}&hardware=${props.row.id}`"
              >
                {{ props.formattedRow[props.column.field] }}
              </router-link>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </span>
            <span
              v-else-if="props.column.field === 'licence'"
            >
              <router-link
                v-if="props.formattedRow['licence']"
                :to="`/licence/update/${props.formattedRow['licence'].id}`"
              >
                {{ props.formattedRow['licence'].name }}
              </router-link>
            </span>
            <span v-else-if="props.column.field === 'is_online'">
              <template>
                <b-badge
                  v-if="props.row.model.manufacturer.name === 'ZyXEL'"
                  pill
                  variant="secondary"
                  class="clickable"
                  @click="handleZyXELInfo"
                >N/A</b-badge>
                <b-badge
                  v-else-if="hardwareRefreshStatus[props.row.id] === 'loading'"
                  pill
                  variant="secondary"
                  class="mr-1"
                >{{ props.formattedRow[props.column.field] ? 'Online' : 'Offline' }}</b-badge>
                <b-badge
                  v-else-if="props.formattedRow[props.column.field] === '-' || !props.formattedRow[props.column.field]"
                  pill
                  variant="danger"
                  class="mr-1"
                >Offline</b-badge>
                <b-badge
                  v-else
                  pill
                  variant="success"
                  class="mr-1"
                >Online</b-badge>
              </template>
              <span v-if="hardwareRefreshStatus[props.row.id] === 'loading'">
                <feather-icon
                  icon="LoaderIcon"
                  size="16"
                  class="spinner-icon"
                />
              </span>
              <span
                v-if="hardwareRefreshStatus[props.row.id] === 'failed'"
                v-b-tooltip.hover
                title="Failed to refresh"
              >
                <feather-icon
                  icon="XCircleIcon"
                  size="16"
                  class="text-danger"
                />
              </span>
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
          <!-- pagination -->
          <div
            slot="pagination-bottom"
          >
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap">
                  Showing 1 to
                </span>
                <b-form-select
                  v-model="perPage"
                  :options="pageSizeOptions"
                  class="mx-1"
                  @change="perPageChanged"
                />
                <span class="text-nowrap"> of {{ pagination.total }} entries </span>
              </div>
              <div>
                <b-pagination
                  v-if="pagination.last_page !== 1"
                  :value="page"
                  :total-rows="pagination.total"
                  :per-page="perPage"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @change="changePage"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </div>
            </div>
          </div>
        </vue-good-table>
      </b-card-body>
    </div>
    <b-modal
      id="modalDeleteLoader"
      ref="modalDeleteLoader"
      :title="`Disassociating in progress...`"
      hide-footer
      centered
      hide-close
      no-close-on-esc
      no-close-on-backdrop
      hide-header-close
      size="sm"
    >
      <div class="d-flex justify-content-center align-items-center mt-2 mb-2">

        <b-spinner
          variant="primary"
          label="Spinning"
          style="width: 3rem; height: 3rem;"
          huge
        />
      </div>
    </b-modal>

    <b-modal
      id="removeHardwarePopUp"
      ref="removeHardwarePopUp"
      title="Are you sure you want to dissociate this hardware?"
      size="lg"
      ok-title="Disassociate"
      ok-only
      centered
      @ok="deassociateItem"
      @hide="hardwareToDelete = null"
    >
      <p>This action only disassociates the hardware from the current Site.</p>
      <p>It will <b>not be deleted</b> from your company account and will remain available in the Inventory tab.</p>
    </b-modal>

    <b-modal
      id="zyxelInfo"
      ref="zyxelInfo"
      title="Notice for ZyXEL Clients"
      centered
      ok-only
      ok-variant="secondary"
      ok-title="Cancel"
    >
      <div class="mt-2 mb-2">

        <p>Remote access is not supported for ZyXEL ODUs. These devices can only be accessed onsite or through a support ticket.</p>
        <p>If you require assistance, please contact your supplier, and we will be happy to help.</p>
        <p>Thank you for your understanding.</p>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCardBody, BCard, BPagination, BDropdown, BDropdownItem, BButton, BFormSelect, BSpinner, BBadge, VBTooltip,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import IndexPagesMixin from '@/mixins/IndexPages.vue'
import { mapActions, mapState } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import router from '@/router'
import TableMobileSolo from '@/components/TableMobileSolo.vue'
import AddHardwareModal from './AddHardwareModal.vue'
import axiosIns from '../../axios-resolver'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BCardBody, BCard, BPagination, VueGoodTable, BDropdown, BDropdownItem, BButton, BFormSelect, BSpinner, BBadge, AddHardwareModal, TableMobileSolo,
  },
  mixins: [
    IndexPagesMixin,
  ],
  data() {
    return {
      id: this.$route.params.id,
      uppdateRouteName: 'hardware-update',
      oldItems: [],
      searchValue: '',
      mobileSearchFields: {
        name: '',
        'model.name': '',
        serial_number: '',
        comment: '',
      },
      hardwareRefreshStatus: {},
      fields: [
        {
          field: 'name',
          label: 'Name',
          filterOptions: {
            enabled: true,
            placeholder: 'Name',
            filterValue: '',
          },
        },
        {
          field: 'model.name',
          mobileSpecial: 'model',
          label: 'Model',
          filterOptions: {
            enabled: true,
            placeholder: 'Model',
            filterValue: '',
          },
        },
        {
          field: 'serial_number',
          label: 'Serial No.',
          filterOptions: {
            enabled: true,
            placeholder: 'Serial No.',
            filterValue: ''
            ,
          },
        },
        {
          field: 'current_system_version',
          label: 'Firmware Version',
          width: '110px',
          filterOptions: {
            enabled: true,
            placeholder: 'Version',
            filterValue: '',
          },
        },
        {
          field: 'is_online',
          label: 'Online Status',
          filterOptions: {
            enabled: true,
            placeholder: 'Status',
            filterValue: '',
            filterDropdownItems: [
              { value: 1, text: 'Online' },
              { value: 0, text: 'Offline' },
            ],
          },
        },
        {
          field: 'licence',
          label: 'Licence №',
          mobileSpecial: 'licence',
          filterOptions: {
            enabled: true,
            placeholder: 'Licence №',
            filterValue: '',
          },
        },
        {
          field: 'comment',
          label: 'Comment',
          filterOptions: {
            enabled: true,
            placeholder: 'Comment',
            filterValue: '',
          },
        },
        {
          field: 'ip_address',
          label: 'IP Address',
          filterOptions: {
            enabled: true,
            placeholder: 'IP Address',
            filterValue: '',
          },
        },
        {
          field: 'action',
          label: 'Action',
          sortable: false,
          thClass: 'action-th-width',
          hidden: !this.$can('update', 'hardware') && !this.$can('delete', 'hardware'),
        },
      ],
      hardwareToDelete: null,
      isLoading: true,
      statusTimeout: null,
    }
  },
  computed: {
    ...mapState('status', {
      items: 'items',
      pagination: 'pagination',
      searchFields: 'searchFields',
      orderBy: 'orderBy',
      sortedBy: 'sortedBy',
    }),
    isRefreshing() {
      return Object.values(this.hardwareRefreshStatus).some(status => status === 'loading')
    },
  },
  watch: {
    hardwareToDelete() {
      console.log('this.hardwareToDelete', this.hardwareToDelete)

      if (this.hardwareToDelete) {
        this.$refs.removeHardwarePopUp.show()
      } else {
        this.$refs.removeHardwarePopUp.hide()
      }
    },
    items() {
      clearTimeout(this.statusTimeout)
      if (this.items.length > 0) {
        const ids = this.items.map(item => item.id)

        const checkStatus = () => {
          if (router.currentRoute.params.id) {
            this.statusTimeout = setTimeout(async () => {
              await this.handleRunCheckStatus(ids)
              this.doesOnlineStatusChange()
              checkStatus()
            }, 15_000)
          } else {
            clearTimeout(this.statusTimeout)
          }
        }

        checkStatus()
      }
    },
  },
  destroyed() {
    clearTimeout(this.statusTimeout)
  },
  beforeRouteLeave(to, from, next) {
    clearTimeout(this.statusTimeout)
    next()
  },
  methods: {
    ...mapActions('sites', [
      'getSiteSummary',
    ]),
    ...mapActions('status', {
      fetchItems: 'getItems',
      setSearchFields: 'setSearchFields',
      setOrderBy: 'setOrderBy',
      setSortedBy: 'setSortedBy',
      handleDeassociateItem: 'deassociateItem',
      handleRunCheckStatus: 'runCheckStatus',
    }),
    ...mapActions('hardware', {
      deassociateItems: 'deassociateItems',
    }),
    doesOnlineStatusChange() {
      if (this.oldItems.length > 0) {
        this.items.forEach(item => {
          const oldItem = this.oldItems.find(oldItemProp => oldItemProp.id === item.id)

          if (oldItem && oldItem.is_online !== item.is_online) {
            this.getSiteSummary(this.id)
          }
        })
      }
      this.oldItems = this.items
    },
    toSwitchPorts(hardwareId) {
      this.$router.push({ name: 'switch-ports', query: { site: this.id, hardware: hardwareId } })
    },
    toLteStats(hardwareId) {
      this.$router.push({ name: 'lte-hardware-summary', params: { hardwareId, id: this.id } })
    },
    handleZyXELInfo() {
      this.$refs.zyxelInfo.show()
    },
    openNewWindow(url) {
      window.open(url, '_blank')
    },
    handleReset() {
      this.resetFilter()
      this.searchValue = ''
    },
    async handleMobileSearch(search, searchValue) {
      if (this.search !== searchValue) {
        this.searchValue = searchValue
      }

      this.columnFilterFn(search, true)
    },
    async deassociateItem() {
      try {
        this.$refs.modalDeleteLoader.show()
        await this.handleDeassociateItem(this.hardwareToDelete)
        this.hardwareToDelete = null
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!2',
            icon: 'BellIcon',
            variant: 'success',
          },
        })
        await this.fetchItems()
      } catch (error) {
        const { status } = error.response
        if (!status) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Server error',
              icon: 'BellIcon',
              variant: 'danger',
            },
          })
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.response.data.message,
              icon: 'BellIcon',
              variant: 'danger',
            },
          })
        }
      } finally {
        this.$refs.modalDeleteLoader.hide()
      }
    },
    async refreshHardwareStatus() {
      const hardwareIds = this.items.map(item => item.id)

      hardwareIds.forEach(hardwareId => {
        this.$set(this.hardwareRefreshStatus, hardwareId, 'loading')
      })

      const requests = hardwareIds.map(hardwareId => axiosIns.put(`/site/${this.id}/hardware/status`, {
        hardwareIds: [hardwareId],
        manualExecution: 1,
      })
        .then(response => {
          this.$set(this.hardwareRefreshStatus, hardwareId, 'success')

          if (response.data && response.data.data && response.data.data.length > 0) {
            response.data.data.forEach(updatedHardware => {
              const itemIndex = this.items.findIndex(item => item.id === updatedHardware.id)
              if (itemIndex !== -1) {
                this.$set(this.items[itemIndex], 'is_online', updatedHardware.is_online)
              }
            })
          }
        })
        .catch(error => {
          this.$set(this.hardwareRefreshStatus, hardwareId, 'failed')
          console.log(`Hardware ${hardwareId} refresh error:`, error)
        }))

      await Promise.allSettled(requests)
    },
  },
}
</script>

<style lang="scss">
  .spinner-icon {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
.hardware-table-status {
  overflow-x: auto;
}
.no-padding {
  .card-body{
    padding: 0;
  }
}
</style>
